import React, { useContext, useMemo, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import styled, { css } from 'styled-components';
import { toast } from 'react-toastify';
import produce from 'immer';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
  TargetingDeviceType,
  toTargetingDeviceType,
} from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { InputModal } from '@/components/input-modal';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { capitalize } from '@/utils/types';
import { getDeviceBasedEditorId } from '@/webapi/common';
import { fakeClickChange } from '@/features/editor/widgets/fake-click';
import { AutomationChange, CompoundChange, WidgetChange } from '@/pkg/sdk';
import { DeviceType, Role } from '@/utils/definitions';
import { AccountContext } from '@/features/account-context';
import { upgradeWidgetChange } from '@/features/editor/widgets/shared/apps-catalog/upgrade-widget';
import {
  StyledTooltip,
  TooltipRow,
} from '@/features/editor/widgets/changelog/shared/shared';
import { useAiMetadataApi } from '@/features/ai-agents/shared/use-ai-metadata-api';
import { getQueryParam } from '@/utils/browser';
import { Spinner } from '@/Spinner';

export async function cloneChange(
  change: EditorDeclarativeBlock,
  duplicateSession?: (
    agentId: string,
    sessionId: string,
  ) => Promise<{ new_session_id: string }>,
) {
  return produce(change, async (draft) => {
    const id = `_loomi_addon_${new Date().getTime()}`;
    const oldId = draft.editorId;
    draft.editorId = id;
    draft.editorSelector = `#${id}`;
    draft.block.value[`htmlKind`] = `appendAfter`;
    draft.block.selector = `#vsly-invalid-selector`;
    if (draft.block.value?.[`env`]) {
      draft.block.value[`env`][`sectionId`] = draft.editorSelector;
    }

    const compound = draft?.block?.value as CompoundChange;
    if (compound?.html) {
      compound.html = compound.html.replaceAll(oldId, id);
    }
    if (compound?.css) {
      compound.css = compound.css.replaceAll(oldId, id);
    }
    if (compound?.js) {
      compound.js = compound.js.replaceAll(oldId, id);
    }

    if (
      duplicateSession &&
      compound?.aiSessionId &&
      compound?.selectedAgent?.id &&
      compound?.isAiComponent
    ) {
      try {
        const { new_session_id: newSessionId } = await duplicateSession(
          compound.selectedAgent.id,
          compound.aiSessionId,
        );
        compound.aiSessionId = newSessionId;
      } catch (error) {
        console.error(`Failed to duplicate AI session:`, error);
      }
    }
  });
}

export function ChangelogTooltip({
  change,
  isSynced,
}: {
  change: EditorDeclarativeBlock;
  isSynced: boolean;
}) {
  const {
    account: {
      store: { role },
    },
  } = useContext(AccountContext);

  const isSystemUser = useMemo(() => role === Role.SYSTEM, [role]);

  const { duplicateSession } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const {
    resources: { appsCatalog },
    currentExtension,
    transpiler: { globalCssId, globalJsId, asHideElementBlock },
    experienceState: {
      upsertEditorChange,
      removeEditorChange,
      currentExperience,
    },
    inspectorNav: { gotoChangeSelector },
    devicePreview: {
      editorState: { device },
      appendElement,
    },
  } = useContext(EditorContext);

  const tooltip = useRef(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const {
    props: renameProps,
    show: showRenameModal,
    hide: hideRenameModal,
    fromRef: renameRef,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `transparent`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(23, 42),
  );

  const isCheckoutExt = useMemo(
    () => currentExtension === `shopify-checkout-extensibility`,
    [currentExtension],
  );

  const isSameDeviceAsChange = useMemo(
    () => change?.targetDevice === toTargetingDeviceType(device),
    [device, change],
  );

  const oppositeDevice = useMemo<DeviceType>(() => {
    if (device === DeviceType.Desktop) return DeviceType.Mobile;
    return DeviceType.Desktop;
  }, [device]);

  const isTargetingAllDevices = useMemo<boolean>(
    () => change?.targetDevice === TargetingDeviceType.ALL_DEVICES,
    [change],
  );

  const onDelete = async (ev) => {
    ev.stopPropagation();
    removeEditorChange(change);
  };

  const onUpgradeWidget = async (ev) => {
    ev.stopPropagation();
    const newChange = upgradeWidgetChange(change, appsCatalog);
    if (newChange) {
      upsertEditorChange(newChange);
    }
  };

  const onReplacePosition = async (ev) => {
    ev.stopPropagation();
    gotoChangeSelector(change);
  };

  const onCssSelectorCopy = async (ev) => {
    ev.stopPropagation();
    await navigator.clipboard.writeText(change.block.selector);
    toast(`Selector Copied Successfully`, {
      theme: `colored`,
      type: `success`,
    });
  };

  const onCopyWidgetId = async (ev) => {
    ev.stopPropagation();
    await navigator.clipboard.writeText(
      (change.block.value as WidgetChange).env[`sectionId`],
    );
    toast(`Selector Copied Successfully`, {
      theme: `colored`,
      type: `success`,
    });
  };

  const onDuplicate = async (ev) => {
    ev.stopPropagation();
    const compoundValue = change.block.value as CompoundChange;
    const isAiComponent =
      compoundValue?.aiSessionId &&
      compoundValue?.selectedAgent?.id &&
      compoundValue?.isAiComponent;

    setIsDuplicating(true);
    try {
      const clone = await cloneChange(change, duplicateSession);
      setIsDuplicating(false);
      gotoChangeSelector(clone, true);
    } catch (error) {
      console.error(`Failed to duplicate change:`, error);
      setIsDuplicating(false);
      toast(`Failed to duplicate ${isAiComponent ? `AI session` : `widget`}`, {
        theme: `colored`,
        type: `error`,
      });
    }
  };

  const onShowRename = (ev: any) => {
    ev.stopPropagation();
    hideTooltip();
    showRenameModal();
  };

  const onToggleTargetAllDevices = (ev: any) => {
    ev.stopPropagation();
    hideTooltip();
    const newChange = produce(change, (draft) => {
      if (draft.targetDevice === TargetingDeviceType.DESKTOP) {
        normalizeDesktopValues(draft);
      }
      draft.targetDevice = isTargetingAllDevices
        ? toTargetingDeviceType(device)
        : TargetingDeviceType.ALL_DEVICES;
    });
    upsertEditorChange(newChange);
  };

  const onCopyToDevice = (ev: any) => {
    ev.stopPropagation();
    hideTooltip();
    const isSameDevice = change.targetDevice === toTargetingDeviceType(device);
    const toDevice = isSameDevice ? oppositeDevice : device;
    if (change.editorKind === EditorChangeKind.GLOBAL_CSS) {
      const newChange = produce(change, (draft) => {
        draft.editorId = globalCssId(currentExperience.id, toDevice);
        draft.targetDevice = toTargetingDeviceType(toDevice);
      });
      upsertEditorChange(newChange);
    } else if (change.editorKind === EditorChangeKind.GLOBAL_JS) {
      const newChange = produce(change, (draft) => {
        draft.editorId = globalJsId(currentExperience.id, toDevice);
        draft.targetDevice = toTargetingDeviceType(toDevice);
      });
      upsertEditorChange(newChange);
    } else if (change.editorKind === EditorChangeKind.NEW_COMPONENT) {
      const clone = produce(change, (draft) => {
        if (isSameDevice && toDevice === DeviceType.Mobile) {
          normalizeDesktopValues(draft);
        }
        const id = `_loomi_addon_${new Date().getTime()}`;
        const oldId = draft.editorId;
        draft.editorId = id;
        draft.editorSelector = `#${id}`;
        if (draft.block.value?.[`env`]) {
          draft.block.value[`env`][`sectionId`] = draft.editorSelector;
        }
        const value = draft.block.value as CompoundChange;
        if (value?.html) {
          value.html = value.html.replaceAll(oldId, id);
        }
        if (value?.css) {
          value.css = value.css.replaceAll(oldId, id);
        }
        if (value?.js) {
          value.js = value.js.replaceAll(oldId, id);
        }
        draft.targetDevice = toTargetingDeviceType(toDevice);
      });
      upsertEditorChange(clone, false, true);
    } else if (change.editorKind === EditorChangeKind.FAKE_CLICK) {
      const clone = produce(change, (draft) => {
        const id = getDeviceBasedEditorId(
          change.block.selector,
          toDevice,
          currentExperience.experienceCreationVersion,
        );

        const newChange = fakeClickChange(
          id,
          (change.block.value as AutomationChange).steps,
        );
        draft.editorId = newChange.editorId;
        draft.editorSelector = newChange.editorSelector;
        draft.targetDevice = toTargetingDeviceType(toDevice);
      });
      upsertEditorChange(clone);
    } else if (change.editorKind === EditorChangeKind.HIDE_COMPONENT) {
      const clone = produce(change, (draft) => {
        const newChange = asHideElementBlock(change.editorSelector, toDevice);
        draft.editorId = newChange.editorId;
        draft.editorSelector = newChange.editorSelector;
        draft.targetDevice = toTargetingDeviceType(toDevice);
      });
      upsertEditorChange(clone);
    } else {
      toast(`Oops, can't copy this change`, {
        theme: `colored`,
        type: `error`,
        className: css({
          fontFamily: `JetBrains Mono, Serif`,
          fontWight: `700`,
        }),
      });
    }
  };

  const hideTooltip = () => {
    setTimeout(() => {
      tooltip.current.tooltipRef.style.display = `none`;
    }, 100);
    setTimeout(() => {
      tooltip.current.tooltipRef.style.display = `block`;
    }, 500);
  };

  const onRename = (name: string) => {
    const updated = produce(change, (draft) => {
      draft.hint = name;
    });
    upsertEditorChange(updated);
    hideRenameModal();
  };

  const onNewContent = (ev, kind) => {
    ev.stopPropagation();
    if (change.editorKind === EditorChangeKind.NEW_COMPONENT) {
      appendElement(change.editorSelector, kind);
    }
  };

  return (
    <>
      <SharedElementOverlay {...renameProps}>
        <InputModal
          title="Rename Change?"
          description="Give a meaningful name for this change"
          yesCaption="Rename"
          noCaption="Discard"
          placeholder=""
          initialValue={change.hint}
          onConfirm={onRename}
          onDiscard={hideRenameModal}
        />
      </SharedElementOverlay>
      <StyledTooltip />
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <ReactTooltip
        ref={tooltip}
        id={change.editorId}
        place="left"
        effect="solid"
        clickable
        backgroundColor="#ffffff"
        textColor="#111111"
        arrowColor="#ffffff"
        className="exp-options-tooltip-cls"
        scrollHide
        delayHide={200}
        event="click"
        eventOff="mouseout"
      >
        <Wrapper>
          {isSynced && (
            <TooltipRow onClick={(e) => onNewContent(e, `appendBefore`)}>
              Add Before
            </TooltipRow>
          )}
          {isSynced && (
            <TooltipRow onClick={(e) => onNewContent(e, `appendAfter`)}>
              Add After
            </TooltipRow>
          )}
          {isSynced &&
            !isCheckoutExt &&
            change.editorKind === EditorChangeKind.NEW_COMPONENT && (
              <TooltipRow onClick={onDuplicate}>
                <DuplicateContent>
                  <span>Duplicate</span>
                  {isDuplicating && (
                    <Spinner
                      border={1.5}
                      size="16"
                      mt="0"
                      relative
                      color="#111111"
                    />
                  )}
                </DuplicateContent>
              </TooltipRow>
            )}
          {isSynced &&
            !isCheckoutExt &&
            change.editorKind === EditorChangeKind.NEW_COMPONENT && (
              <TooltipRow onClick={onReplacePosition}>
                {(change?.block?.value as CompoundChange)?.selectedAgent?.id ===
                `section-modifier`
                  ? `Change Section`
                  : `Replace Position`}
              </TooltipRow>
            )}
          {[
            EditorChangeKind.NEW_COMPONENT,
            EditorChangeKind.EDIT_COMPOUND,
            EditorChangeKind.HIDE_COMPONENT,
          ].includes(change.editorKind) &&
            !isCheckoutExt && (
              <>
                <TooltipRow onClick={onCssSelectorCopy}>
                  Copy CSS Selector
                </TooltipRow>
                {!!change?.widgetProps?.appId && (
                  <TooltipRow onClick={onCopyWidgetId}>
                    Copy Widget Id
                  </TooltipRow>
                )}
                <TooltipRow ref={renameRef} onClick={onShowRename}>
                  Rename
                </TooltipRow>
              </>
            )}
          {!isTargetingAllDevices &&
            !isCheckoutExt &&
            [
              EditorChangeKind.GLOBAL_CSS,
              EditorChangeKind.GLOBAL_JS,
              EditorChangeKind.NEW_COMPONENT,
              EditorChangeKind.HIDE_COMPONENT,
              EditorChangeKind.FAKE_CLICK,
            ].includes(change.editorKind) && (
              <TooltipRow onClick={onCopyToDevice}>
                Copy to{` `}
                {capitalize(isSameDeviceAsChange ? oppositeDevice : device)}
              </TooltipRow>
            )}
          {[
            EditorChangeKind.GLOBAL_CSS,
            EditorChangeKind.GLOBAL_JS,
            EditorChangeKind.NEW_COMPONENT,
            EditorChangeKind.HIDE_COMPONENT,
            EditorChangeKind.FAKE_CLICK,
          ].includes(change.editorKind) && (
            <TooltipRow onClick={onToggleTargetAllDevices}>
              Target{` `}
              {isTargetingAllDevices ? capitalize(device) : `All Devices`}
            </TooltipRow>
          )}
          <TooltipRow onClick={onDelete}>Delete</TooltipRow>
          {isSystemUser && change?.block?.kind === `widget` && (
            <TooltipRow onClick={onUpgradeWidget}>Upgrade to latest</TooltipRow>
          )}
        </Wrapper>
      </ReactTooltip>
    </>
  );
}

const Wrapper = styled.div`
  && > div:not(:last-child) {
    border-bottom: 1px solid #f1f1f1;
  }
`;

const DuplicateContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

function normalizeDesktopValues(obj) {
  try {
    if (typeof obj !== `object` || obj === null) return obj;
    Object.entries(obj).forEach(([key, value]) => {
      if (key === `desktop` && typeof value === `object`) {
        Object.assign(obj, value);
        // Optional behavior
        // delete obj.desktop;
      } else if (typeof value === `object` && value !== null) {
        obj[key] = normalizeDesktopValues(value);
      }
    });
    return obj;
  } catch {
    return obj;
  }
}
